/**
 * useFinishedActivity — le crochet d'une activité finie, rouverte pour être relue : il branche la
 * machine (`finishedActivity`) sur l'API et la télémétrie.
 *
 * Ce qui part d'ici, comme de l'ancien `ActivityFinishedScreen` et de sa boîte « Mon rendu » :
 *  - à l'ouverture, la copie relue (`getActivities`, `with_logs`, l'activité seule) : le réducteur
 *    de l'activité courante la range, l'écran la lit dans le store ;
 *  - toutes les 5 s, 10 minutes au plus (`PAGE_POLLS`), tant que la copie attend sa note ou que Sam
 *    la corrige, et seulement l'écran au premier plan (`active`). L'ancienne boîte relançait un second
 *    relevé, pareil, tant qu'elle était ouverte : un seul suffit ;
 *  - la correction marquée lue (`read-review`), une fois par activité. L'ancien écran la renvoyait à
 *    chaque relecture de l'activité, tant que le serveur ne disait pas qu'elle l'était ;
 *  - l'avis sur la correction, relu à l'ouverture du rendu (`workOpen`), donné d'un geste ; un
 *    envoi qui échoue rend l'avis d'avant et se dit (l'ancien laissait l'avis affiché comme donné) ;
 *  - « Refaire l'activité » (`redo`) : la même activité redonnée (`activityTodoAsStudent`), posée en
 *    activité courante « en cours » ; l'écran passe alors à son exécution, comme avant.
 *  - un fil d'Ariane à chaque changement d'état, une erreur typée à chaque échec, aucun `catch` vide.
 */
import { useCallback, useEffect, useMemo, useRef } from "react"
import { useDispatch } from "react-redux"

import { setCurrentActivity } from "../../../data/redux/actions/activity"
import { activitiesService } from "../../../data/services/activitiesService"
import { apiCall, apiFailureOf, type ApiService } from "../../shared/apiCall"
import { createTelemetry, type EngineTelemetry } from "../../shared/telemetry"
import { type PhaseTransition, useSignalReducer, withPhaseTransitions } from "../../shared/useSignalReducer"
import { useCorrectionPolling } from "../correction/useCorrectionPolling"
import {
    finishedActivityPhase,
    finishedActivityPolls,
    finishedActivityReducer,
    finishedCorrection,
    initialFinishedActivityState,
    needsReviewRead,
    type CorrectionFeedbackValue,
    type FinishedActivity,
    type FinishedActivityState,
    type FinishedCorrection,
} from "./finishedActivity"

/** Les appels d'une activité finie (les tests passent les leurs). */
export type FinishedActivityServices = {
    /** La copie relue, journaux compris : le store la range. */
    refresh: (activityId: number) => Promise<unknown>
    readReview: (activityId: number) => Promise<unknown>
    readFeedback: (activityId: number) => Promise<{ feedback_type?: CorrectionFeedbackValue | null } | null>
    submitFeedback: (activityId: number, value: CorrectionFeedbackValue) => Promise<unknown>
    /** La même activité redonnée : la nouvelle, telle que le serveur la sert. */
    redo: (body: Record<string, unknown>) => Promise<Record<string, unknown> | null>
}

export type UseFinishedActivityOptions = {
    activity: FinishedActivity | null
    /** L'écran est au premier plan : la copie ne se relit pas sous un autre écran. */
    active: boolean
    /** Le rendu est ouvert : son avis se relit. */
    workOpen: boolean
    /**
     * L'activité refaite, posée « en cours » : l'appelant l'ouvre là où il est. Le live la montre dans
     * son volet et prévient le tuteur ; hors live, l'écran de la route passe seul à l'exécution.
     */
    onRedone?: (fresh: { id?: number | null } & Record<string, unknown>) => void
    services?: FinishedActivityServices
    telemetry?: EngineTelemetry
}

export type FinishedActivityHandle = {
    state: FinishedActivityState
    correction: FinishedCorrection
    submitFeedback: (value: CorrectionFeedbackValue) => void
    /** « Refaire l'activité », avec le corps que l'écran a composé. */
    redo: (body: Record<string, unknown>) => void
    dismissNotice: () => void
}

type Dispatch = (action: never) => unknown

const defaultServices = (dispatch: Dispatch): FinishedActivityServices => ({
    refresh: (activityId) =>
        apiCall(dispatch, activitiesService.getActivities as ApiService, {
            urlParams: { with_logs: 1, activity_ids: activityId },
        }),
    readReview: (activityId) =>
        apiCall(dispatch, activitiesService.readReview as ApiService, { routeParams: { id: activityId } }),
    readFeedback: (activityId) =>
        apiCall(dispatch, activitiesService.getActivityCorrectionFeedback as ApiService, {
            routeParams: { activityId },
        }),
    submitFeedback: (activityId, value) =>
        apiCall(dispatch, activitiesService.submitActivityCorrectionFeedback as ApiService, {
            data: { activityId, feedbackType: value },
        }),
    redo: (body) => apiCall(dispatch, activitiesService.activityTodoAsStudent as ApiService, { data: body }),
})

/**
 * Le relevé de la page dure plus que l'attente (90 s) : la page reste ouverte pendant que Sam corrige
 * (en Dialogue, l'élève y revient sans attendre), et son bouton passe à « Voir ma correction » quand
 * elle arrive. En prod, sur les 14 jours avant le 26/10, une correction sur dix arrivait après 2 min 24,
 * et près de 98 sur 100 en 10 min.
 */
const PAGE_POLLS = 120

const reduceWithTransitions = withPhaseTransitions(finishedActivityReducer, finishedActivityPhase)

/** Le message que le serveur a écrit, s'il en a écrit un. */
const serverMessage = (error: unknown): string | null => apiFailureOf(error)?.message ?? null

export function useFinishedActivity(options: UseFinishedActivityOptions): FinishedActivityHandle {
    const { activity, active, workOpen } = options
    const onRedoneRef = useRef(options.onRedone)
    onRedoneRef.current = options.onRedone
    const activityId = activity?.id ?? null
    const dispatchRedux = useDispatch() as unknown as Dispatch

    const telemetry = useMemo(
        () => options.telemetry ?? createTelemetry("activity.finished", { role: "student" }),
        // La télémétrie d'un écran monté ne change pas en route.
        // eslint-disable-next-line react-hooks/exhaustive-deps
        []
    )
    const services = useMemo(
        () => options.services ?? defaultServices(dispatchRedux),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [dispatchRedux]
    )

    const [stored, dispatch, read] = useSignalReducer(
        reduceWithTransitions,
        () => initialFinishedActivityState(activityId),
        (transitions: PhaseTransition[]) => {
            for (const { from, to, event } of transitions) telemetry.transition(from, to, event)
        }
    )
    useEffect(() => {
        dispatch({ type: "activity", activityId })
    }, [activityId, dispatch])
    // L'activité passée fait foi au rendu même : une autre ne montre jamais l'état de la précédente.
    const state = stored.activityId === activityId ? stored : initialFinishedActivityState(activityId)

    const refresh = useCallback(() => {
        const id = read().activityId
        if (id == null) return
        dispatch({ type: "loadStarted" })
        services
            .refresh(id)
            .catch((error: unknown) => {
                telemetry.error("refresh", error)
            })
            .finally(() => {
                if (read().activityId === id) dispatch({ type: "loadEnded" })
            })
    }, [dispatch, read, services, telemetry])

    // La copie relue à l'ouverture de chaque activité.
    useEffect(() => {
        if (activityId != null) refresh()
    }, [activityId, refresh])

    useCorrectionPolling(active && finishedActivityPolls(activity), refresh, undefined, PAGE_POLLS)

    // La correction marquée lue, une fois par activité.
    const reviewReadFor = useRef<number | null>(null)
    const unread = needsReviewRead(activity)
    useEffect(() => {
        if (activityId == null || !unread || reviewReadFor.current === activityId) return
        reviewReadFor.current = activityId
        services.readReview(activityId).catch((error: unknown) => {
            telemetry.error("read_review", error)
            // Seul un refus du serveur se dit (l'ancien écran montrait son message) ; une coupure se tait.
            const message = serverMessage(error)
            if (message != null && read().activityId === activityId) dispatch({ type: "reviewReadFailed", message })
        })
    }, [activityId, unread, services, telemetry, dispatch, read])

    // L'avis sur la correction, relu à l'ouverture du rendu.
    useEffect(() => {
        if (!workOpen || activityId == null) return
        services
            .readFeedback(activityId)
            .then((data) => {
                if (read().activityId === activityId)
                    dispatch({ type: "feedbackRead", value: data?.feedback_type ?? null })
            })
            .catch((error: unknown) => {
                telemetry.error("feedback_read", error)
            })
    }, [workOpen, activityId, services, telemetry, dispatch, read])

    const submitFeedback = useCallback(
        (value: CorrectionFeedbackValue) => {
            const current = read()
            const id = current.activityId
            if (id == null || current.feedbackSubmitting) return
            dispatch({ type: "feedbackGiven", value })
            services.submitFeedback(id, value).then(
                () => {
                    if (read().activityId === id) dispatch({ type: "feedbackSettled" })
                },
                (error: unknown) => {
                    telemetry.error("feedback_submit", error)
                    if (read().activityId === id) dispatch({ type: "feedbackFailed", message: serverMessage(error) })
                }
            )
        },
        [dispatch, read, services, telemetry]
    )

    const redo = useCallback(
        (body: Record<string, unknown>) => {
            const id = read().activityId
            if (id == null || read().redoing) return
            dispatch({ type: "redoStarted" })
            services
                .redo(body)
                .then((fresh) => {
                    if (read().activityId !== id) return
                    dispatch({ type: "redoDone" })
                    if (fresh == null) return
                    // L'ancien lanceur, sur la route de l'activité : l'activité courante, « en cours ».
                    dispatchRedux(setCurrentActivity({ activityData: fresh, progressStatus: "ongoing" }) as never)
                    onRedoneRef.current?.(fresh as { id?: number | null } & Record<string, unknown>)
                })
                .catch((error: unknown) => {
                    telemetry.error("redo", error)
                    if (read().activityId === id) dispatch({ type: "redoFailed", message: serverMessage(error) })
                })
        },
        [dispatch, dispatchRedux, read, services, telemetry]
    )

    const dismissNotice = useCallback(() => dispatch({ type: "noticeDismissed" }), [dispatch])

    return {
        state,
        correction: finishedCorrection(activity),
        submitFeedback,
        redo,
        dismissNotice,
    }
}
